import { Col, Button, Form, Card } from "react-bootstrap";
import "../../../App.css";
import "../../../css/bootstrap.scss";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  setwizardPageNumber,
  setwizardPageName,
  setCheckPage,
} from "../../EntityAccountReducer";
import { notifyError } from "MyComponents/Toaster";
import { updateEntityKycDraftStep, uploadEntityKycDraftFiles } from "../../../Utils/entityKycDraft";
import CountryDropdown from "MyComponents/UI/CountryDropdown";
import countriesStateData from "../../../data/states.json";
import countriesData from "../../../data/countries.json";
import MultiFileSelector from "./FilePicker";
import { YearPicker, MonthPicker, DayPicker } from "react-dropdown-date";

interface UploadedFile {
  filename: string;
  url: string;
}

interface Director {
  officialTitle: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  streetAddress: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  identityDocumentUrl?: string;
}

const DirectorForm: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userIDFromRedux = useSelector((state: RootState) => state.entityAccount.userID);
  const { userId: userIdFromUrl } = useParams<{ userId?: string }>();
  const ssoUserID = userIDFromRedux || userIdFromUrl || "";
  const checkPage = useSelector((state: RootState) => state.entityAccount.checkPage);

  const [directors, setDirectors] = useState<Director[]>([]);
  const [showAddForm, setShowAddForm] = useState(true);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Form states
  const [isLoading, setIsLoading] = useState(false);
  const [officialTitle, setOfficialTitle] = useState("");
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState({ year: "", month: "", day: "" });
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  
  const [uploadedDocument, setUploadedDocument] = useState<UploadedFile[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const [errors, setErrors] = useState<any>({});

  const invites = useSelector((state: RootState) => state.entityAccount.invites);

  useEffect(() => {
    dispatch(setwizardPageName("directors"));
  }, [dispatch]);

  useEffect(() => {
    if (invites && invites.length > 0) {
      const existingDirectors = invites.filter((inv: any) => inv?.role === 'director' || inv?.Role === 'director' || inv?.userRole === 'director');
      if (existingDirectors.length > 0) {
        setDirectors(existingDirectors as any);
        setShowAddForm(false);
      }
    }
  }, [invites]);

  const resetForm = () => {
    setOfficialTitle(""); setFirstName(""); setMiddleName(""); setLastName(""); setEmail("");
    setDate({ year: "", month: "", day: "" });
    setStreetAddress(""); setCity(""); setSelectedState(""); setPostalCode(""); setSelectedCountry("");
    setUploadedDocument([]); setPendingFiles([]);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors: any = {};
    if (!officialTitle) newErrors.officialTitle = "Official Title is required";
    if (!firstName) newErrors.firstName = "First name is required";
    if (!lastName) newErrors.lastName = "Last name is required";
    if (!email) newErrors.email = "Email is required";
    if (!date.year || !date.month || !date.day) newErrors.dob = "Date of Birth is required";
    if (!streetAddress) newErrors.streetAddress = "Street address is required";
    if (!city) newErrors.city = "City is required";
    if (!selectedState) newErrors.state = "State is required";
    if (!postalCode) newErrors.postalCode = "Postal code is required";
    if (!selectedCountry) newErrors.country = "Country is required";
    
    if (uploadedDocument.length === 0 && pendingFiles.length === 0) {
      newErrors.document = "Identity document is required";
    }

    setErrors(newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEdit = (idx: number) => {
    const director = directors[idx];
    setOfficialTitle(director.officialTitle);
    setFirstName(director.firstName);
    setMiddleName(director.middleName || "");
    setLastName(director.lastName);
    setEmail(director.email);
    const [year, month, day] = director.dateOfBirth.split("-");
    setDate({ year, month, day });
    setStreetAddress(director.streetAddress);
    setCity(director.city);
    setSelectedState(director.state);
    setPostalCode(director.postalCode);
    setSelectedCountry(director.country);
    if (director.identityDocumentUrl) {
      setUploadedDocument([{ filename: 'Uploaded Document', url: director.identityDocumentUrl }]);
    } else {
      setUploadedDocument([]);
    }
    setPendingFiles([]);
    setEditingIndex(idx);
    setShowAddForm(true);
  };

  const handleRemove = async (idx: number) => {
    if (window.confirm("Are you sure you want to remove this director?")) {
      const updatedDirectors = directors.filter((_, i) => i !== idx);
      setDirectors(updatedDirectors);
      if (editingIndex === idx) {
        setEditingIndex(null);
        resetForm();
        setShowAddForm(false);
      }
      await updateEntityKycDraftStep(ssoUserID, "directors", updatedDirectors);
    }
  };

  const handleAddDirector = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      let updatedDocuments = [...uploadedDocument];
      if (pendingFiles.length > 0) {
        const uploadedNewFiles = await uploadEntityKycDraftFiles(ssoUserID, pendingFiles);
        if (uploadedNewFiles) {
          updatedDocuments = [...updatedDocuments, ...uploadedNewFiles];
        }
      }

      const newDirector: Director = {
        officialTitle,
        firstName,
        middleName,
        lastName,
        email,
        dateOfBirth: date.year + "-" + date.month + "-" + date.day,
        streetAddress,
        city,
        state: selectedState,
        postalCode,
        country: selectedCountry,
        identityDocumentUrl: updatedDocuments[0]?.url || ""
      };

      if (editingIndex !== null) {
        const updatedDirectors = [...directors];
        updatedDirectors[editingIndex] = newDirector;
        setDirectors(updatedDirectors);
        setEditingIndex(null);
        await updateEntityKycDraftStep(ssoUserID, "directors", updatedDirectors);
      } else {
        const updatedDirectors = [...directors, newDirector];
        setDirectors(updatedDirectors);
        await updateEntityKycDraftStep(ssoUserID, "directors", updatedDirectors);
      }

      resetForm();
      setShowAddForm(false);
    } catch (error) {
      console.error(error);
      notifyError("Failed to add director");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextStep = async () => {
    if (directors.length === 0 && !showAddForm) {
      notifyError("Please add at least one Director");
      return;
    }
    if (checkPage === 'SummaryPage') {
      dispatch(setCheckPage(''));
      dispatch(setwizardPageName("summary"));
      dispatch(setwizardPageNumber(7));
    } else {
      // In this flow, after directors we usually go to documents or certification (page 7 usually)
      dispatch(setwizardPageName("summary"));
      dispatch(setwizardPageNumber(7));
    }
  };

  return (
    <div className="pt-0 pb-3 mb-5 col-md-11 mx-auto px-2">
      <div className="row justify-content-center">
        <Col md={11}>
          <p className="fw-bold d-inline">{t('kyc.entity_account_app')}</p>
          <p className="text-warning d-inline ms-3 small">{t('common.in_progress')}</p>
          <h4 className="mt-3">Directors</h4>
          <p className="text-secondary small">
            Please provide details for all directors.
          </p>
        </Col>
      </div>

      <div className="row justify-content-center mt-3">
        <Col md={11}>
          {directors.map((director, idx) => (
            <Card key={idx} className="mb-3 rounded-4 shadow-sm border-0">
              <Card.Body className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">{director.firstName} {director.lastName}</h5>
                  <p className="text-primary mb-0">{director.officialTitle}</p>
                  <p className="text-muted small mb-0">{director.email}</p>
                </div>
                <div>
                  <Button variant="outline-primary" size="sm" className="me-2 rounded-5 px-3" onClick={() => handleEdit(idx)}>Edit</Button>
                  <Button variant="outline-danger" size="sm" className="rounded-5 px-3" onClick={() => handleRemove(idx)}>Remove</Button>
                </div>
              </Card.Body>
            </Card>
          ))}
          
          {!showAddForm && (
            <div className="d-flex justify-content-between align-items-center mt-2 mb-4">
              <Button variant="outline-primary" className="rounded-5 px-4" onClick={() => setShowAddForm(true)}>
                + Add Another Director
              </Button>
              {directors.length > 0 && (
                <Button className="btn btn-success rounded-5" onClick={handleNextStep} style={{ minWidth: '200px' }}>
                  Continue
                </Button>
              )}
            </div>
          )}

          {showAddForm && (
            <div className="bg-light p-4 rounded-4 mb-4">
              <h5 className="mb-4">{editingIndex !== null ? "Edit Director" : "Add Director"}</h5>
              <div className="row mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Official Title *</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="text" placeholder="e.g. Executive Director, Non-executive Director" value={officialTitle} onChange={e => setOfficialTitle(e.target.value)} isInvalid={!!errors.officialTitle} />
                  </Form.Group>
                </Col>
              </div>

              <div className="row mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold">First Name *</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} isInvalid={!!errors.firstName} />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Middle Name</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="text" value={middleName} onChange={e => setMiddleName(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Last Name *</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="text" value={lastName} onChange={e => setLastName(e.target.value)} isInvalid={!!errors.lastName} />
                  </Form.Group>
                </Col>
              </div>

              <div className="row mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Email Address *</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="email" value={email} onChange={e => setEmail(e.target.value)} isInvalid={!!errors.email} />
                  </Form.Group>
                </Col>
              </div>

              <div className="row mb-3 mt-4">
                <Col md={12}>
                    <label className="form-label fw-bold">Date of Birth *</label>
                    <div className="d-flex align-items-center">
                        <div className="me-3">
                            <MonthPicker defaultValue={'Month'} numeric short caps endYearGiven year={date.year as any} value={date.month as any} onChange={(m: any) => setDate({ ...date, month: m })} classes={'form-select rounded-5 py-3'} />
                        </div>
                        <div className="me-3">
                            <DayPicker defaultValue={'Date'} year={date.year as any} month={date.month as any} endYearGiven value={date.day as any} onChange={(d: any) => setDate({ ...date, day: d })} classes={'form-select rounded-5 py-3'} />
                        </div>
                        <div>
                            <YearPicker defaultValue={'Year'} start={1900} end={new Date().getFullYear()} reverse value={date.year as any} onChange={(y: any) => setDate({ ...date, year: y })} classes={'form-select rounded-5 py-3'} />
                        </div>
                    </div>
                    {errors.dob && <small className="text-danger">{errors.dob}</small>}
                </Col>
              </div>

              <h6 className="mt-4">Address Details</h6>
              <div className="row mb-3 mt-3">
                <Col md={6}>
                  <Form.Group>
                    <CountryDropdown selectedCountry={selectedCountry} handleCountryClick={setSelectedCountry} countryValidation={!!errors.country} countriesData={countriesData} title="Country *" />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mt-4">
                    <Form.Label className="fw-bold">State / Province *</Form.Label>
                    <div className="dropdown">
                      <button className="btn w-100 bg-white border text-start d-flex justify-content-between align-items-center py-2 rounded-5" type="button" data-bs-toggle="dropdown">
                        {selectedState || "Select State"} <i className="fas fa-chevron-down text-muted"></i>
                      </button>
                      <ul className="dropdown-menu w-100 shadow" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {countriesStateData.map((c: any) => 
                          c.name === selectedCountry && c.states?.map((s: any) => (
                            <li key={s.id} onClick={() => setSelectedState(s.name)}>
                              <span className="dropdown-item" style={{ cursor: 'pointer' }}>{s.name}</span>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                    {errors.state && <small className="text-danger">{errors.state}</small>}
                  </Form.Group>
                </Col>
              </div>
              <div className="row mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">City *</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="text" value={city} onChange={e => setCity(e.target.value)} isInvalid={!!errors.city} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Postal Code *</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="text" value={postalCode} onChange={e => setPostalCode(e.target.value)} isInvalid={!!errors.postalCode} />
                  </Form.Group>
                </Col>
              </div>
              <div className="row mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Street Address *</Form.Label>
                    <Form.Control className="rounded-5 py-2" type="text" value={streetAddress} onChange={e => setStreetAddress(e.target.value)} isInvalid={!!errors.streetAddress} />
                  </Form.Group>
                </Col>
              </div>

              <div className="row mb-4 mt-3">
                <Col md={12}>
                  <Form.Label className="fw-bold">Identity Document *</Form.Label>
                  <MultiFileSelector
                    maxFiles={1}
                    maxFileSizeMB={20}
                    existedFiles={uploadedDocument}
                    onFilesChange={(f: any) => setUploadedDocument(f)}
                    onPendingFilesChange={(f: any) => setPendingFiles(f)}
                  />
                  {errors.document && <small className="text-danger">{errors.document}</small>}
                </Col>
              </div>

              <div className="d-flex justify-content-end gap-3 mt-4">
                {directors.length > 0 && (
                  <Button variant="outline-secondary" className="rounded-5 px-4" onClick={() => { setShowAddForm(false); setEditingIndex(null); resetForm(); }}>
                    Cancel
                  </Button>
                )}
                <Button className="btn btn-primary rounded-5 px-4" onClick={handleAddDirector} disabled={isLoading}>
                  {isLoading ? "Saving..." : "Save Director"}
                </Button>
              </div>
            </div>
          )}



        </Col>
      </div>
    </div>
  );
};

export default DirectorForm;
